import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Textarea } from 'components/Form/Input'
import { Select, Option } from 'components/Select/Select'
import { CalendarInput } from 'components/CalendarInput/CalendarInput'
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from 'components/ModalV2/Modal'
import { ColorVariant, Flex } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { tokens } from 'styling/tokens'
import { Heading2, NormalText, SmallText, TinyText } from 'styling/TypographyV2'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { AsyncSelect } from 'components/Select/AsyncSelect'
import { baseUrl } from 'utils/axios'
import {
  assignUsersToTeam,
  getOperationalTeamDetails,
  getSimpleOperationalTeams,
} from 'features/operationalManagement/operationalManagementSlice'
import { getUserCurrentAssignment } from 'features/assignments/assignmentsSlice'
import {
  AssignmentStatus,
  IOperationalAssignment,
} from 'features/assignments/assignments.types'
import { IBasicUserSimple } from 'features/operationalManagement/operationalManagement.types'
import { PeopleGroupIcon } from '../icons'
import {
  CurrentAssignmentBox,
  CurrentAssignmentLabel,
  CurrentAssignmentSectionLabel,
  ReadonlyField,
  ReadonlyFieldGrid,
  ReadonlyFieldLabel,
} from '../Organogram.styled'

interface CreateAssignmentModalProps {
  onClose: () => void
  onCreated?: () => void
}

const ShieldIcon = (
  <svg width='14' height='14' viewBox='0 0 20 20' fill='none'>
    <path
      fill='currentColor'
      d='M10 1.5l-7 3V10c0 4.3 3 7.7 7 8.5 4-.8 7-4.2 7-8.5V4.5l-7-3z'
    />
  </svg>
)

const CalendarPillIcon = (
  <svg width='14' height='14' viewBox='0 0 20 20' fill='none'>
    <path
      fill='currentColor'
      d='M4 3V1h2v2h8V1h2v2h2v14H2V3h2zm0 6v7h12V9H4z'
    />
  </svg>
)

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${d.getFullYear()}`
}

const fmtSupervisors = (list: { user: { firstName: string; lastName: string } }[]): string => {
  if (!list || list.length === 0) return '—'
  return list.map((s) => `${s.user.firstName} ${s.user.lastName}`.trim()).join(', ')
}

export const CreateAssignmentModal = ({ onClose, onCreated }: CreateAssignmentModalProps) => {
  const dispatch = useDispatch<AppDispatch>()

  const assigning = useSelector(
    (state: RootState) => state.operationalManagement.assigningEmployees,
  )
  const simpleTeams = useSelector(
    (state: RootState) => state.operationalManagement.simpleTeams,
  )
  const teamDetailsById = useSelector(
    (state: RootState) => state.operationalManagement.teamDetailsById,
  )

  const [employeeOption, setEmployeeOption] = useState<Option | null>(null)
  const [targetTeamId, setTargetTeamId] = useState<number | null>(null)
  const employeeId =
    employeeOption?.value == null ? null : (employeeOption.value as number)
  const [effectiveStart, setEffectiveStart] = useState<Date | null>(null)
  const [effectiveEnd, setEffectiveEnd] = useState<Date | null>(null)
  const [reason, setReason] = useState('')

  const [currentAssignment, setCurrentAssignment] = useState<IOperationalAssignment | null>(null)
  const [loadingCurrent, setLoadingCurrent] = useState(false)

  useEffect(() => {
    dispatch(getSimpleOperationalTeams(undefined))
  }, [dispatch])

  useEffect(() => {
    if (targetTeamId != null && !teamDetailsById[targetTeamId]) {
      dispatch(getOperationalTeamDetails(targetTeamId))
    }
  }, [dispatch, targetTeamId, teamDetailsById])

  useEffect(() => {
    if (employeeId == null) {
      setCurrentAssignment(null)
      return
    }
    let cancelled = false
    setLoadingCurrent(true)
    const promise = dispatch(getUserCurrentAssignment(employeeId))
    promise
      .then((result) => {
        if (cancelled) return
        if (getUserCurrentAssignment.fulfilled.match(result)) {
          const list = result.payload
          const active =
            list.find((a) => a.status === AssignmentStatus.Active && !a.endDate) ??
            list.find((a) => a.status === AssignmentStatus.Active) ??
            list[0] ??
            null
          setCurrentAssignment(active)
          if (active?.operationalTeam?.id) {
            dispatch(getOperationalTeamDetails(active.operationalTeam.id))
          }
        } else {
          setCurrentAssignment(null)
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCurrent(false)
      })
    return () => {
      cancelled = true
      promise.abort()
    }
  }, [dispatch, employeeId])

  const teamOptions = useMemo<Option[]>(
    () => simpleTeams.map((t) => ({ label: t.name, value: t.id })),
    [simpleTeams],
  )

  const selectedTeamOption = teamOptions.find((o) => o.value === targetTeamId) ?? null

  const targetDetails = targetTeamId != null ? teamDetailsById[targetTeamId] : undefined

  const currentTeamDetails = currentAssignment?.operationalTeam?.id
    ? teamDetailsById[currentAssignment.operationalTeam.id]
    : undefined

  const canSave =
    employeeId != null &&
    targetTeamId != null &&
    effectiveStart != null &&
    reason.trim() !== '' &&
    !assigning

  const handleSave = async () => {
    if (!canSave || !targetTeamId || employeeId == null || !effectiveStart) return
    const result = await dispatch(
      assignUsersToTeam({
        userIds: [employeeId],
        operationalTeamId: targetTeamId,
        startDate: effectiveStart.toISOString(),
        ...(effectiveEnd ? { endDate: effectiveEnd.toISOString() } : {}),
        reason: reason.trim(),
      }),
    )
    if (assignUsersToTeam.fulfilled.match(result)) {
      onCreated?.()
      onClose()
    }
  }

  const currentTeam = currentAssignment?.operationalTeam?.name ?? null
  const currentStart = currentAssignment?.startDate ?? null
  const currentEnd = currentAssignment?.endDate ?? null
  const placeholderDash = loadingCurrent ? '…' : '—'

  return (
    <Modal onClose={onClose}>
      <ModalContent maxWidth='520px'>
        <ModalHeader>
          <Flex between center>
            <Heading2>Create assignment</Heading2>
            <CloseIcon onClick={onClose} />
          </Flex>
          <NormalText mt='0.5rem' colorVariant={ColorVariant.SECONDARY}>
            Assign an employee to a team with full audit trail
          </NormalText>
        </ModalHeader>
        <ModalBody>
          <CurrentAssignmentBox>
            <CurrentAssignmentSectionLabel>Current Assignment</CurrentAssignmentSectionLabel>
            <CurrentAssignmentLabel>{<PeopleGroupIcon size={14} />} Current Team</CurrentAssignmentLabel>
            <div>{currentTeam ?? placeholderDash}</div>
            <CurrentAssignmentLabel>{ShieldIcon} Supervisors</CurrentAssignmentLabel>
            <div>
              {currentTeamDetails
                ? fmtSupervisors(currentTeamDetails.supervisors)
                : placeholderDash}
            </div>
            <CurrentAssignmentLabel>{CalendarPillIcon} Effective start</CurrentAssignmentLabel>
            <div>{currentStart ? fmtDate(currentStart) : placeholderDash}</div>
            <CurrentAssignmentLabel>{CalendarPillIcon} Effective end</CurrentAssignmentLabel>
            <div style={{ color: !currentEnd && currentAssignment ? tokens.status.success.fg : undefined }}>
              {currentEnd ? fmtDate(currentEnd) : currentAssignment ? 'Open-ended' : placeholderDash}
            </div>
          </CurrentAssignmentBox>

          <Flex column gap='1rem'>
            <AsyncSelect<IBasicUserSimple>
              label='Employee *'
              placeholder='Select employee'
              optionKey='create-assignment-employees'
              value={employeeOption}
              loadOptions={async (q) => {
                const resp = await baseUrl.get<{ data: IBasicUserSimple[] }>('users/basic', {
                  params: { page: 1, limit: 20, state: 1, ...(q ? { q } : {}) },
                })
                return resp.data.data
              }}
              decoder={(u) => ({ label: u.fullName, value: u.id })}
              onChange={(opt) => setEmployeeOption(opt.value == null ? null : opt)}
            />
            <Select
              label='Target team *'
              placeholder='Select team'
              options={teamOptions}
              value={selectedTeamOption}
              onChange={(opt) => setTargetTeamId(opt.value as number)}
            />

            <ReadonlyFieldGrid>
              <ReadonlyField>
                <ReadonlyFieldLabel>{<PeopleGroupIcon size={14} />} PARENT TEAM</ReadonlyFieldLabel>
                <div>{targetDetails?.parentTeam?.name ?? '—'}</div>
              </ReadonlyField>
              <ReadonlyField>
                <ReadonlyFieldLabel>{ShieldIcon} SUPERVISOR</ReadonlyFieldLabel>
                <div>{fmtSupervisors(targetDetails?.supervisors ?? [])}</div>
              </ReadonlyField>
            </ReadonlyFieldGrid>

            <Flex gap='0.75rem'>
              <div style={{ flex: 1 }}>
                <CalendarInput
                  width='100%'
                  labelText='Effective start'
                  required
                  nomarg
                  defaultValue={effectiveStart ?? undefined}
                  onChange={(d) => setEffectiveStart(d instanceof Date ? d : null)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <CalendarInput
                  width='100%'
                  labelText='Effective end (optional)'
                  nomarg
                  defaultValue={effectiveEnd ?? undefined}
                  minDate={effectiveStart ?? undefined}
                  onChange={(d) => setEffectiveEnd(d instanceof Date ? d : null)}
                />
              </div>
            </Flex>
            <TinyText colorVariant={ColorVariant.SECONDARY}>
              Leave end date empty for open-ended assignments – closes automatically upon
              reassignment.
            </TinyText>

            <Textarea
              label='Reason for Assignment *'
              placeholder='e.g. New hire onboarding, transfer from Bravo Crew...'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
            <SmallText colorVariant={ColorVariant.SECONDARY}>
              Required – all changes are logged in the Audit trail.
            </SmallText>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button variant='secondary' onClick={onClose} disabled={assigning}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handleSave} disabled={!canSave}>
            {assigning ? 'Saving…' : '+ Create assignment'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
